import { useEffect, useState, useMemo } from "react";

/**
 * Hook para monitorar o status de carregamento de uma ou mais famílias de fontes.
 * Retorna "loading" | "loaded" | "error".
 */
export function useFontStatus(
  families: string | string[],
  options?: { weights?: number[]; timeoutMs?: number }
) {
  const [status, setStatus] = useState<"loading" | "loaded" | "error">("loading");
  const fams = useMemo(() => Array.isArray(families) ? families : [families], [families]);
  const weights = useMemo(() => options?.weights ?? [400], [options?.weights]);
  const timeoutMs = options?.timeoutMs ?? 10000;

  // Extracted dependencies for useEffect
  const famsKey = fams.join(",");
  const weightsKey = weights.join(",");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");

    // Se FontFaceSet API não existe, considera loaded
    if (typeof document === "undefined" || !("fonts" in document)) {
      setStatus("loaded");
      return;
    }

    // Aguarda todas as famílias/pesos
    const fontPromises = fams.flatMap(fam =>
      weights.map(weight =>
        (document as Document & { fonts: FontFaceSet }).fonts.load(
          `1em "${fam}"`, // font shorthand
          String(weight)   // <-- Corrigido: converte weight para string
        )
      )
    );

    // Timeout de segurança
    const timeout = setTimeout(() => {
      if (!cancelled) setStatus("error");
    }, timeoutMs);

    Promise.all(fontPromises)
      .then(() => {
        if (!cancelled) setStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [fams, weights, timeoutMs, famsKey, weightsKey]);

  return status;
}